import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { Article } from '@/models/article/Article';
import router from '@/router';
import {
  ARTICLE_CREATE,
  ARTICLE_EDIT,
  BROWSE,
  BROWSE_PARAGRAPH,
  BROWSE_SHARED,
  BROWSE_TEXT_TAB,
  DRAFT_EDIT,
} from '@/routerNames';
import apiService from '@/services/ApiService';
import articleEditService from '@/services/article/ArticleEditService';
import notificationService from '@/services/NotificationService';
import store from '@/store';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';
import { dispatchGetPersonalArticlesCount } from '@/store/dispatchers/uiDispatchers';
import { dispatchOpenSelectedArticle } from '@/store/dispatchers/workspaceViewDispatchers';
import { Loading } from 'element-ui';
import { ElLoadingComponent } from 'element-ui/types/loading';
import { Route } from 'vue-router/types/router';
import videoDownloaderService from '@/services/VideoDownloaderService';

/** How often to re-ask the server whether the merged download is ready. */
const DOWNLOAD_POLL_INTERVAL_MS = 3000;
/**
 * How long to keep the download loader up before telling the user to come back.
 * Merging a long recording is minutes of server ffmpeg work; the render carries
 * on regardless, so a later click picks up the finished file from cache.
 */
const DOWNLOAD_MERGE_TIMEOUT_MS = 10 * 60 * 1000;

export class ArticleService {
  getSelectedArticleId(): string {
    if (store.state.workspaceView.selectedArticle) {
      return store.state.workspaceView.selectedArticle.id;
    }

    const article = articleEditService.articleEditor.article;
    return article.original || article.id;
  }

  async duplicateArticle(articleId: string): Promise<void> {
    const { workspaceView } = store.state;
    const res = await apiService.article.duplicate(articleId);
    const { article, workspaceOrder } = res.data;

    workspaceView.articles.push({
      id: article.id,
      isShared: false,
      isHasVideo: !!article.paragraphs.length,
      isHasNotes: !!article.textTabs.length,
      title: article.title,
      isArchived: false,
      shortId: article.shortId,
    });
    workspaceView.workspace.order = workspaceOrder;
    await dispatchGetPersonalArticlesCount();
    await dispatchOpenSelectedArticle({ articleId: article.id });
  }

  async toggleArticleMode(_mode: ArticleEditMode, route: Route) {
    const mode = _mode === ArticleEditMode.NOTES ? ArticleEditMode.NOTES : ArticleEditMode.VIDEO;
    switch (route.name) {
      case DRAFT_EDIT: {
        await router.push({ name: DRAFT_EDIT, params: { mode } });
        break;
      }
      case ARTICLE_EDIT: {
        await router.push({ name: ARTICLE_EDIT, params: { mode } });
        break;
      }
      case BROWSE:
      case BROWSE_TEXT_TAB:
      case BROWSE_PARAGRAPH: {
        const isTextMode = _mode === ArticleEditMode.NOTES;
        const params: { mode: ArticleEditMode; paragraphSlug?: string; textTabSlug?: string } = {
          ...route.params,
          mode,
        };

        if (isTextMode) {
          delete params.paragraphSlug;
        } else {
          delete params.textTabSlug;
        }

        await router.push({ name: isTextMode ? BROWSE : route.name, params });

        break;
      }
      case ARTICLE_CREATE:
      case BROWSE_SHARED: {
        commitSetArticleMode(mode);
        break;
      }
      default: {
        throw new Error(`can not toggle article mode on this route: ${route.name}`);
      }
    }
  }

  /**
   * Wait for the server to resolve the article's recording into ONE file.
   *
   * A recording split across paragraphs is stored as several videos; the server
   * renders the merge in the background, so we poll until it reports `ready`.
   * Returns the download URL, or null when the caller should stop (the wait
   * timed out, or the render failed) — the reason has already been surfaced.
   */
  private async waitForDownloadVideoUrl(articleId: string, onProgress: () => void): Promise<string | null> {
    const deadline = Date.now() + DOWNLOAD_MERGE_TIMEOUT_MS;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data } = await apiService.article.getDownloadVideo(articleId);

      if (data.status === 'ready') {
        return data.url;
      }

      if (data.status === 'failed') {
        notificationService.error('We could not prepare your recording for download. Please try again.');
        return null;
      }

      // The recording itself is still being processed — the videos we would
      // merge are about to change. Don't hold the user behind a loader for
      // that; an optimize job is queued after every save, so this is the normal
      // "just edited the article" case.
      if (data.stage === 'analyzing') {
        notificationService.warning(
          'Your recording is still being processed. Please wait a few minutes before downloading.'
        );
        return null;
      }

      if (Date.now() >= deadline) {
        notificationService.warning(
          'Your recording is still being prepared. Please try downloading again in a few minutes.'
        );
        return null;
      }

      onProgress();
      await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_POLL_INTERVAL_MS));
    }
  }

  async downloadArticleVideo(article: Article) {
    const loader = Loading.service({
      lock: true,
      text: 'Preparing your recording',
    }) as ElLoadingComponent & { text: string };

    let url: string | null = null;

    try {
      url = await this.waitForDownloadVideoUrl(article.id, () => {
        loader.text = 'Preparing a single video file — this can take a few minutes…';
      });
    } catch (err: unknown) {
      loader.close();
      await apiService.handleResponseError(err);
      return;
    }

    if (!url) {
      loader.close();
      return;
    }

    let blob: Blob;

    try {
      blob = await videoDownloaderService.downloadVideo(url, (p) => {
        loader.text = `Downloading: ${p.percent.toFixed(0)}%`;
      });
    } catch (err: unknown) {
      loader.close();
      await apiService.handleResponseError(err);
      return;
    }

    try {
      const extension = blob.type.startsWith('video/')
        ? blob.type.split('/')[1] || 'mp4'
        : videoDownloaderService.getFileExtension(url);

      const a = document.createElement('a');
      document.body.appendChild(a);
      const blobUrl = window.URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = `${article.slug}.${extension}`;
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 0);
    } catch (err: unknown) {
      await apiService.handleResponseError(err);
    } finally {
      loader.close();
    }
  }
}

const articleService = new ArticleService();
export default articleService;
